import React from "react";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { FrontPage } from "../../components/Front/Simple/FrontPage/FrontPage";
import { FrontFormPrimaryButton } from "../../components/Front/Simple/FrontFormPrimaryButton/FrontFormPrimaryButton";
import { required } from "../../../../shared/src/codecs/types/required";
import { string } from "../../../../shared/src/codecs/types/string";
import { boolean } from "../../../../shared/src/codecs/types/boolean";
import { TTypeOfCodec } from "../../../../shared/src/codecs/codec";
import { ReceivesConsumerNps } from "../../../../domain/codecs/UserCaseUserType";

const NpsContextResponseCodec = required({
    users_cases_id: string(),
    first_name: string(),
    address_short_string: string(),
    sold_by_sail_homes: boolean(),
    already_answered: boolean(),
    trustpilot_url: string(),
    user_type: string(),
});
type TContextResponse = TTypeOfCodec<typeof NpsContextResponseCodec>;

type TPhase =
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "scoring"; context: TContextResponse; score: number | null; feedback: string; submitting: boolean }
    | { kind: "thanks"; context: TContextResponse; score: number };

type TState = {
    phase: TPhase;
};

const CONSUMER_USER_TYPES = new Set<string>(ReceivesConsumerNps);

class NpsPage extends React.Component<React.PropsWithChildren<TContainerStateProps>, TState> {
    public state: TState = { phase: { kind: "loading" } };

    private getUsersCasesId = (): string =>
        this.props.state.routes.params.usersCasesId || "";

    private getInitialScoreFromQuery = (): number | null => {
        const raw = this.props.state.routes.queryParams.VIEW_NPS.score;
        if (!raw) return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) && n >= 0 && n <= 10 ? n : null;
    };

    private isConsumer = (userType: string): boolean =>
        CONSUMER_USER_TYPES.has(userType);

    componentDidMount(): void {
        this.loadContext();
        window.addEventListener("pageshow", this.handlePageShow);
    }

    componentWillUnmount(): void {
        window.removeEventListener("pageshow", this.handlePageShow);
    }

    private handlePageShow = (e: PageTransitionEvent): void => {
        if (e.persisted) {
            this.setState({ phase: { kind: "loading" } });
            this.loadContext();
        }
    };

    private loadContext = (): void => {
        const id = this.getUsersCasesId();
        if (!id) {
            this.setState({ phase: { kind: "error", message: "Invalid link." } });
            return;
        }

        fetch(`${env.REACT_APP_API_URL}/web/nps/${id}/context`, {
            credentials: "include",
            headers: { "accept": "application/json", "content-type": "application/json" },
        })
            .then((r) => {
                if (r.status === 401) {
                    window.location.href = `${env.REACT_APP_AUTH_URL}/web/login?redirect_url=${encodeURIComponent(window.location.href)}`;
                    return null;
                }
                if (!r.ok) throw new Error(`status ${r.status}`);
                return r.json();
            })
            .then((raw: unknown): TContextResponse | null => {
                if (raw === null) return null;
                const decoded = NpsContextResponseCodec.decode(raw);
                if (decoded._tag === "Right") return decoded.right;
                throw new Error("NPS context response failed schema validation");
            })
            .then((data) => {
                if (!data) return;
                if (data.already_answered) {
                    this.setState({ phase: { kind: "thanks", context: data, score: 0 } });
                    return;
                }
                this.setState({
                    phase: {
                        kind: "scoring",
                        context: data,
                        score: this.getInitialScoreFromQuery(),
                        feedback: "",
                        submitting: false,
                    },
                });
            })
            .catch(() => {
                this.setState({ phase: { kind: "error", message: "We couldn't load your survey. The link may have expired." } });
            });
    };

    private selectScore = (score: number): void => {
        if (this.state.phase.kind !== "scoring") return;
        this.setState({ phase: { ...this.state.phase, score } });
    };

    private setFeedback = (feedback: string): void => {
        if (this.state.phase.kind !== "scoring") return;
        this.setState({ phase: { ...this.state.phase, feedback } });
    };

    private submit = (): void => {
        if (this.state.phase.kind !== "scoring") return;
        const phase = this.state.phase;
        if (phase.score === null) return;

        this.setState({ phase: { ...phase, submitting: true } });

        const id = this.getUsersCasesId();
        const isIntroducer = phase.context.user_type === "introducer";
        const path = isIntroducer
            ? `/web/nps/introducer/${id}/submit`
            : `/web/nps/consumer/${id}/submit`;

        fetch(`${env.REACT_APP_API_URL}${path}`, {
            method: "POST",
            credentials: "include",
            headers: {
                "accept": "application/json",
                "content-type": "application/json",
            },
            body: JSON.stringify({
                score: phase.score,
                feedback_text: phase.feedback,
            }),
        })
            .then((r) => {
                if (!r.ok && r.status !== 204) throw new Error(`status ${r.status}`);
                this.setState({ phase: { kind: "thanks", context: phase.context, score: phase.score as number } });
            })
            .catch(() => {
                this.setState({ phase: { ...phase, submitting: false } });
            });
    };

    public render(): JSX.Element {
        return (
            <FrontPage>
                <div className="nps-page">
                    {this.renderBody()}
                </div>
            </FrontPage>
        );
    }

    private renderBody(): JSX.Element {
        const phase = this.state.phase;

        if (phase.kind === "loading") {
            return <p className="nps-page__lead">Loading…</p>;
        }

        if (phase.kind === "error") {
            return <p className="nps-page__message">{phase.message}</p>;
        }

        if (phase.kind === "thanks") {
            return this.renderThanks(phase);
        }

        return this.renderScoring(phase);
    }

    private renderScoring(phase: Extract<TPhase, { kind: "scoring" }>): JSX.Element {
        const { context, score, feedback, submitting } = phase;
        return (
            <>
                <h1 className="nps-page__title">
                    {context.first_name ? `Hi ${context.first_name},` : "Hi,"}
                </h1>
                <p className="nps-page__lead">
                    Thanks for trusting us with your move{context.address_short_string ? ` at ${context.address_short_string}` : ""}. We've got one quick question — it takes 30 seconds.
                </p>
                <p className="nps-page__question">
                    How likely are you to recommend Sail Legal to a friend or family member?
                </p>
                <div className="nps-page__scores">
                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <button
                            key={n}
                            type="button"
                            onClick={() => this.selectScore(n)}
                            className={
                                "nps-page__score-button" +
                                (score === n ? " nps-page__score-button--selected" : "")
                            }
                        >
                            {n}
                        </button>
                    ))}
                </div>
                <div className="nps-page__scale-labels">
                    <span>Not at all likely</span>
                    <span>Extremely likely</span>
                </div>
                <label className="nps-page__feedback-label" htmlFor="nps-feedback">
                    Anything you'd like to share? (optional)
                </label>
                <textarea
                    id="nps-feedback"
                    className="nps-page__feedback-textarea"
                    value={feedback}
                    onChange={(e) => this.setFeedback(e.target.value)}
                />
                <div className="nps-page__submit-row">
                    <FrontFormPrimaryButton
                        label={submitting ? "Submitting…" : "Submit"}
                        isDisabled={score === null || submitting}
                        onClick={this.submit}
                    />
                </div>
            </>
        );
    }

    private renderThanks(phase: Extract<TPhase, { kind: "thanks" }>): JSX.Element {
        const { context, score } = phase;
        const showTrustpilot =
            this.isConsumer(context.user_type)
            && score >= 9
            && !!context.trustpilot_url;

        return (
            <div className="nps-page__message">
                <h1 className="nps-page__title">Thanks{context.first_name ? `, ${context.first_name}` : ""}!</h1>
                {showTrustpilot
                    ? <>
                        <p>So glad we did right by you. If you have a moment, would you mind leaving us a public review?</p>
                        <a className="nps-page__trustpilot-cta" href={context.trustpilot_url} target="_blank" rel="noopener noreferrer">
                            Leave a Trustpilot review
                        </a>
                    </>
                    : <p>We appreciate you taking the time. Your feedback helps us improve.</p>
                }
            </div>
        );
    }
}

export default NpsPage;
